import * as React from 'react'
import {StateUpdaters, withStateHandlers} from 'recompose'
import {logger, Time, Util} from 'yougile-util'
import {
  DataObjectId,
  DeadlineStickerEntry,
  DeadlineStickerValue,
  extractStickerState,
  Hub,
  isSetSticker,
  TaskData
} from 'yougile-model'
import {Sync} from '../../client/sync'
import {withUpdaters} from '../../containers/updaters'
import {LocationUpdater} from '../../containers/updaters/location-updater'
import {SyncUpdater} from '../../containers/updaters/sync-updater'
import {Board} from '../../extensions/board'
import {ChatUtils} from '../../extensions/chat-utils'
import {Column} from '../../extensions/column'
import {globals} from '../../extensions/globals-base'
import {PageService} from '../../extensions/page-service'
import {Sticker, stickerCtl} from '../../extensions/sticker/sticker'
import {taskDataFromHub} from '../../extensions/task-data-extractor'
import {getAllReportTasks} from '../statistics/stats-reports/stats-reports-get-tasks'
import {Handlers, InnerProps, Props, State, TimelineBase} from './timeline-base'
import {TimelinePosition} from './timeline-position'

const day = 24 * 3600_000
const savedState: {
  changedTask: TaskData
} = {
  changedTask: undefined
}

const handlers: StateUpdaters<Props & InnerProps, State, Handlers> = {
  setStart: state => start => ({...state, start}),
  setStop: state => stop => ({...state, stop}),
  setChangedTask: state => (changedTask, dragPoint) => {
    savedState.changedTask = changedTask
    return {
      ...state,
      changedTask,
      dragPoint: dragPoint ? dragPoint : state.dragPoint
    }
  },
  setHighlight: state => (range, hoveredTaskId) => ({
    ...state,
    highlight: range,
    hoveredTaskId
  }),
  setMaxUnscheduled: state => maxUnscheduled => ({...state, maxUnscheduled})
}

const today = Time.middleOfDay(Time.now())

const Comp = withStateHandlers(
  {
    start: today - 40 * day,
    stop: today + 40 * day,
    changedTask: undefined,
    dragPoint: 0,
    highlight: undefined,
    maxUnscheduled: 100,
    hoveredTaskId: undefined
  },
  handlers
)(TimelineBase)

const TimelineComponent = (props: Props & {showTasks: TaskData[]}) => {
  const box = Sync.getCurrentSandbox()
  const tasks: TaskData[] = Util.removeDuplicateIds(props.showTasks)

  const taskClick = (id: DataObjectId) => () => {
    const task = box.get(id) as Hub
    if (task) {
      ChatUtils.toggleChat(task)
    }
  }
  const deadline = stickerCtl.Deadline.getSticker(box)

  const applyChange = (next: () => void) =>
    setTimeout(() => {
      const {changedTask} = savedState
      if (!changedTask || !deadline) {
        return
      }
      const task = tasks.find(t => t && t.id === changedTask.id)
      const taskHub = box.get(task.id) as Hub
      if (!taskHub) {
        return
      }
      if (!isSetSticker(changedTask.deadline)) {
        return
      }
      if (!isSetSticker(task.deadline)) {
        const current: DeadlineStickerEntry = {
          by: globals.currentUserId,
          deadline: changedTask.deadline,
          startDate: extractStickerState(changedTask.startDate),
          order: changedTask.timelineDepth,
          timestamp: Time.now(),
          withTime: false,
          notifyBefore: 0
        }
        const val: DeadlineStickerValue = {current, history: [current]}
        Sticker.pin(deadline, 'stats', taskHub, val)
      } else {
        const oldValue = taskHub.stickers[deadline.id] as DeadlineStickerValue
        const newEntry: DeadlineStickerEntry = {
          ...oldValue.current,
          by: globals.currentUserId,
          deadline:
            Time.middleOfDay(changedTask.deadline) +
            oldValue.current.deadline -
            Time.middleOfDay(oldValue.current.deadline),
          timestamp: Time.now(),
          order: changedTask.timelineDepth,
          startDate: extractStickerState(changedTask.startDate)
        }
        const newValue: DeadlineStickerValue = {
          current: newEntry,
          history: [...oldValue.history, newEntry]
        }
        Sticker.pin(deadline, 'stats', taskHub, newValue)
      }

      // position other tasks properly
      //
      const changedTasks = tasks
        .filter(t => !!t && isSetSticker(t.deadline))
        .map(t => (t.id === changedTask.id ? changedTask : t))
      const positions = TimelinePosition.removeEmptyLines(
        TimelinePosition.getPositions(changedTasks, changedTask.id),
        1
      )
      changedTasks.forEach(t => {
        const pos = TimelinePosition.getPosition(t)
        const realPos = positions.get(t.id)
        if (pos.depth !== realPos.depth) {
          const hub = box.get(t.id) as Hub
          if (!hub) {
            return
          }
          const oldVal = hub.stickers[deadline.id] as DeadlineStickerValue
          const newEntry: DeadlineStickerEntry = {
            ...oldVal.current,
            order: realPos.depth
          }
          const newValue: DeadlineStickerValue = {
            current: newEntry,
            history: oldVal.history
          }
          logger.log('reposition', t.title, pos.depth, realPos.depth)
          Sticker.pin(deadline, 'stats', hub, newValue)
        }
      })

      next()
    }, 200)

  return (
    <Comp
      {...props}
      tasks={tasks}
      taskClick={taskClick}
      applyChange={applyChange}
    />
  )
}

export const TimelineWithTasks = withUpdaters(
  LocationUpdater({ignoreChat: true}),
  SyncUpdater({companyType: 'current', filter: '*'})
)(TimelineComponent)

export const Timeline = withUpdaters(
  LocationUpdater({ignoreChat: true}),
  SyncUpdater({companyType: 'current', filter: '*'})
)((props: Props) => {
  const box = Sync.getCurrentSandbox()
  const board = PageService.getCurrentBoard()
  const allTasks: TaskData[] = []
  if (board) {
    Board.listColumns(board).forEach(col => {
      if (col.data.report) {
        getAllReportTasks(box, col.data.report).forEach(t => allTasks.push(t))
      } else {
        Column.listTasks(col, false).forEach(t => {
          allTasks.push(taskDataFromHub(box, t))
        })
      }
    })
  }
  return <TimelineComponent {...props} showTasks={allTasks} />
})
